import { z } from 'zod'

// ─── Auth Schemas ─────────────────────────────────────────────────────────────

export const LoginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
})

export const Verify2FASchema = z.object({
  userId: z.string().uuid('Invalid user ID'),
  code: z
    .string()
    .length(6, 'OTP must be 6 digits')
    .regex(/^\d{6}$/, 'OTP must be numeric'),
})

export const RefreshTokenSchema = z.object({
  refreshToken: z.string().min(1, 'Refresh token required'),
})

// ─── Pagination Schema ────────────────────────────────────────────────────────

export const PaginationSchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(20),
  search: z.string().optional(),
  status: z.string().optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
})

// ─── Vendor Schema ────────────────────────────────────────────────────────────

export const CreateVendorSchema = z.object({
  name: z
    .string()
    .min(1, 'Vendor name is required')
    .max(80, 'Max 80 characters')
    .regex(/^[a-zA-Z0-9\s]+$/, 'No special characters allowed'),
  nickname: z.string().max(80).optional(),
  gstin: z
    .string()
    .regex(
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
      'Invalid GSTIN format',
    )
    .optional(),
  bankAccount: z.string().regex(/^\d{9,18}$/, 'Account number must be 9–18 digits'),
  bankIfsc: z
    .string()
    .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC format — e.g. ICIC0000011'),
  upiVpa: z
    .string()
    .regex(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+$/, 'Invalid UPI VPA format')
    .optional(),
  category: z.enum(['SUPPLIER', 'CONTRACTOR', 'UTILITY', 'RENT', 'OTHER']),
})

export const UpdateVendorSchema = z.object({
  name: z
    .string()
    .min(1, 'Vendor name is required')
    .max(80, 'Max 80 characters')
    .regex(/^[a-zA-Z0-9\s]+$/, 'No special characters allowed')
    .optional(),
  nickname: z.string().max(80).optional(),
  gstin: z
    .string()
    .regex(
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
      'Invalid GSTIN format',
    )
    .optional()
    .or(z.literal('')),
  bankAccount: z.string().regex(/^\d{9,18}$/, 'Account number must be 9–18 digits').optional(),
  bankIfsc: z
    .string()
    .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC format — e.g. ICIC0000011')
    .optional(),
  upiVpa: z
    .string()
    .regex(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+$/, 'Invalid UPI VPA format')
    .optional()
    .or(z.literal('')),
  category: z.enum(['SUPPLIER', 'CONTRACTOR', 'UTILITY', 'RENT', 'OTHER']).optional(),
})

export type UpdateVendorInput = z.infer<typeof UpdateVendorSchema>

// ─── Payment Schema ───────────────────────────────────────────────────────────
// IMPORTANT: amountInRupees input from UI is in RUPEES (e.g. 5000.00)
// Schema transforms to PAISE before storing (multiply by 100)
// This matches ICICI docs — ICICI wants rupees, DB stores paise

export const CreatePaymentSchema = z
  .object({
    paymentMode: z.enum(['NEFT', 'RTGS', 'IMPS', 'FT', 'UPI'], {
      errorMap: () => ({ message: 'Invalid payment mode' }),
    }),
    creditAccount: z.string().regex(/^\d{9,18}$/, 'Account number must be 9–18 digits').optional(),
    creditIfsc: z
      .string()
      .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC — e.g. ICIC0000011')
      .optional(),
    upiVpa: z
      .string()
      .regex(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+$/, 'Invalid UPI VPA')
      .optional(),
    payeeName: z
      .string()
      .min(1, 'Payee name is required')
      .max(80, 'Max 80 characters')
      .regex(/^[a-zA-Z0-9\s]+$/, 'No special characters — ICICI requirement'),
    amountInRupees: z
      .number({
        required_error: 'Amount is required',
        invalid_type_error: 'Amount must be a number',
      })
      .positive('Amount must be positive')
      .max(10000000, 'Maximum amount is ₹1,00,00,000'),
    remarks: z
      .string()
      .max(32, 'Max 32 characters for NEFT compatibility')
      .regex(/^[a-zA-Z0-9\s]*$/, 'No special characters — ICICI requirement')
      .optional(),
    vendorId: z.string().uuid().optional(),
    scheduledDate: z.string().optional(), // DD/MM/YYYY — for future scheduled payments
  })
  .refine(
    (data) => {
      // RTGS minimum ₹2,00,000 — from ICICI docs
      if (data.paymentMode === 'RTGS') return data.amountInRupees >= 200000
      return true
    },
    { message: 'RTGS minimum amount is ₹2,00,000', path: ['amountInRupees'] },
  )
  .refine(
    (data) => {
      // UPI maximum ₹1,00,000 — NPCI limit
      if (data.paymentMode === 'UPI') return data.amountInRupees <= 100000
      return true
    },
    { message: 'UPI maximum amount is ₹1,00,000', path: ['amountInRupees'] },
  )
  .refine(
    (data) => {
      // UPI requires VPA
      if (data.paymentMode === 'UPI') return !!data.upiVpa
      return true
    },
    { message: 'UPI VPA is required for UPI payments', path: ['upiVpa'] },
  )
  .refine(
    (data) => {
      // Non-UPI payments require account number and IFSC
      if (data.paymentMode !== 'UPI') return !!(data.creditAccount && data.creditIfsc)
      return true
    },
    { message: 'Account number and IFSC are required for non-UPI payments', path: ['creditAccount'] },
  )

export type CreatePaymentInput = z.infer<typeof CreatePaymentSchema>

// ─── Approval Schemas ─────────────────────────────────────────────────────────

export const ApprovePaymentSchema = z.object({
  otp: z
    .string()
    .length(6, 'OTP must be exactly 6 digits')
    .regex(/^\d{6}$/, 'OTP must be numeric'),
})

export const RejectPaymentSchema = z.object({
  reason: z
    .string()
    .min(1, 'Rejection reason is required')
    .max(255, 'Max 255 characters'),
})

export type ApprovePaymentInput = z.infer<typeof ApprovePaymentSchema>
export type RejectPaymentInput = z.infer<typeof RejectPaymentSchema>

// ─── User Management Schemas ──────────────────────────────────────────────────

const PASSWORD_RULES = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')

const paymentLimitFields = {
  dailyPaymentLimitRupees: z.number().positive('Must be a positive amount').nullable().optional(),
  transactionLimitRupees: z.number().positive('Must be a positive amount').nullable().optional(),
}

export const CreateUserSchema = z.object({
  email: z.string().email('Invalid email address'),
  name: z.string().min(1, 'Name is required').max(80, 'Max 80 characters'),
  phone: z.string().regex(/^\d{10}$/, 'Phone must be 10 digits').optional(),
  role: z.enum(['ADMIN', 'USER'], {
    errorMap: () => ({ message: 'Invalid role' }),
  }),
  ...paymentLimitFields,
})

export const UpdateUserSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  phone: z.string().regex(/^\d{10}$/).optional(),
  role: z.enum(['ADMIN', 'USER']).optional(),
  isActive: z.boolean().optional(),
  ...paymentLimitFields,
})

export const UpdatePermissionsSchema = z.object({
  permissions: z.array(
    z.object({
      module: z.enum(['DASHBOARD', 'PAYMENTS', 'APPROVALS', 'VENDORS', 'EMPLOYEES', 'USERS']),
      granted: z.boolean(),
    }),
  ).min(1, 'At least one permission required'),
})

export const ActivateAccountSchema = z.object({
  token: z.string().min(1, 'Token required'),
  password: PASSWORD_RULES,
})

export const ChangePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password required'),
  newPassword: PASSWORD_RULES,
})

export type CreateUserInput = z.infer<typeof CreateUserSchema>
export type UpdateUserInput = z.infer<typeof UpdateUserSchema>
export type UpdatePermissionsInput = z.infer<typeof UpdatePermissionsSchema>
export type ActivateAccountInput = z.infer<typeof ActivateAccountSchema>
export type ChangePasswordInput = z.infer<typeof ChangePasswordSchema>

// ─── Amount Helpers ───────────────────────────────────────────────────────────

// Convert rupees input to paise for DB storage
export function rupeesToPaise(rupees: number): number {
  return Math.round(rupees * 100)
}

// Convert paise from DB to rupees for display
export function paiseToRupees(paise: number): number {
  return paise / 100
}

// Format paise as Indian currency string — "₹5,00,000.00"
export function formatAmount(paise: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(paise / 100)
}
